// Activity (item 6): challenges I've sent and how they turned out — accepted,
// declined, still pending or expired — newest first. Live badges: a presence dot
// when the target is a member, and a "waiting at the board" flag when they're
// sitting in an accepted game with me yet to arrive. Refreshes on presence
// deltas and inbound Ready-signals (accept/decline) so outcomes land without a
// manual reload.

import { api, type SentChallenge } from "../api.ts";
import { esc, h } from "../dom.ts";
import { t, localeTag } from "../i18n/index.ts";
import { navigate, currentToken, isCurrent } from "../router.ts";
import { currentUser } from "../session.ts";
import { onPresence, onSessionMessage } from "../presence.ts";
import { topNav } from "../ui/topnav.ts";
import { pagedList } from "../ui/pagedList.ts";
import { onDisconnect } from "../ui/lifecycle.ts";
import { statusLabel } from "./activityStatus.ts";

export function activityScreen(): HTMLElement {
  const user = currentUser();
  if (!user) {
    navigate("/login");
    return h(`<main class="card"><p class="msg">${t("common.redirecting")}</p></main>`);
  }

  const el = h(`
    <main class="card wide">
      <section class="panel">
        <h2>${t("activity.title")}</h2>
        <p class="tagline">${t("activity.tagline")}</p>
        <div class="activity-slot"></div>
      </section>
    </main>
  `);
  el.prepend(topNav("activity"));

  // "Is this still the current screen?" — guards the live-refresh path below
  // against firing for a screen the router has already superseded.
  const token = currentToken();

  // Paged load via the shared helper (plan A5). The empty state carries a CTA
  // link, which the helper's plain-text `emptyText` can't — render it into the
  // rows container from onPage instead.
  const rows = h(`<div class="activity-list"></div>`);
  const paged = pagedList<SentChallenge>({
    rowsContainer: rows,
    fetchPage: (offset, limit) =>
      api.sentChallenges(limit, offset).then((r) => ({ items: r.challenges, has_more: r.has_more })),
    renderRow: makeRow,
    errorText: t("activity.loadFailed"),
    onPage: (items, append) => {
      if (!append && items.length === 0) {
        rows.innerHTML =
          `<p class="fine">${t("activity.empty1")}` +
          `<a data-link href="/players">${t("activity.challengeSomeone")}</a></p>`;
      }
    },
  });
  el.querySelector<HTMLElement>(".activity-slot")!.append(paged.element);

  // Live refresh: a presence delta changes online/waiting badges; an inbound
  // accept/decline Ready-signal changes an outcome. Debounce both to one refetch.
  let timer: number | undefined;
  const cleanup = () => {
    offPresence();
    offMessage();
    if (timer) window.clearTimeout(timer);
  };
  const schedule = () => {
    if (!isCurrent(token)) return;
    if (timer) return;
    timer = window.setTimeout(() => {
      timer = undefined;
      void paged.reload();
    }, 600);
  };
  const offPresence = onPresence(schedule);
  const offMessage = onSessionMessage((m) => {
    if (m.t === "ready") schedule();
  });

  // Unsubscribe the moment the screen leaves the DOM (plan A10). The element is
  // still detached while this function runs — attach the watcher once the
  // router has mounted it (mounting happens before the next frame).
  requestAnimationFrame(() => {
    if (!el.isConnected) {
      cleanup();
      return;
    }
    onDisconnect(el, cleanup);
  });

  return el;
}

function makeRow(c: SentChallenge): HTMLElement {
  const label = statusLabel(c.status);
  const dot =
    c.online != null
      ? `<span class="presence-dot ${c.online ? "on" : ""}" title="${c.online ? t("players.online") : t("players.offline")}"></span>`
      : "";
  const waiting = c.waiting ? `<span class="act-waiting">${t("activity.atBoard")}</span>` : "";
  const row = h(`
    <div class="activity-row status-${esc(c.status)}">
      <span class="act-status pill">${esc(label)}</span>
      <span class="act-opp">
        ${dot}<span class="act-name">${esc(c.opponent)}</span>${waiting}
      </span>
      <span class="act-when">${esc(fmtDate(c.created_at))}</span>
    </div>
  `);
  if (c.game_id) {
    const go = document.createElement("a");
    go.className = "btn ghost sm";
    go.setAttribute("data-link", "");
    go.href = `/game/${encodeURIComponent(c.game_id)}`;
    go.textContent = c.waiting ? t("activity.joinNow") : t("activity.openGame");
    row.append(go);
  }
  return row;
}

function fmtDate(unix: number): string {
  const d = new Date(unix * 1000);
  return d.toLocaleDateString(localeTag(), { day: "numeric", month: "short" });
}
